import { Button, Col, Row } from "antd";
import { Content } from "antd/es/layout/layout";
import { useEffect, useMemo } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loader } from "../../Common/Components";
import Page from "../../Common/Page";
import PermissionCheck from "../../Common/PermissionCheck";
import FormControl from "../../components/Form/FormControl";
import { StyledFormInputs, StyledFormSelect } from "../../components/Form/StyledFormInputs";
import countryCodesData from "../../config/countryCodes";
import { useCreateLeague, useFetchLeague, useUpdateLeague } from "../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";

const PermissionName = PermissionNames.LEAGUE;

type CountryCodeEntry = [string, string];
type CountryCodes = CountryCodeEntry[];

const countryCodes: CountryCodes = countryCodesData as CountryCodes;

enum RegionEnum {
  INTERNATIONAL = "INTERNATIONAL",
  NATIONAL = "NATIONAL",
  INTERSTATE = "INTERSTATE",
  STATE = "STATE",
  LOCAL = "LOCAL",
}

interface IFormInput {
  nameLocal: string;
  abbreviationLocal: string;
  nameLatin: string;
  abbreviationLatin: string;
  countryCode: string;
  regionType: RegionEnum;
}

const ManageLeague = () => {
  const { dataId, customerId, sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IFormInput>();
  const navigate = useNavigate();
  const { mutateAsync: createFn, isPending: isCreatePending } = useCreateLeague();
  const { mutate: updateFn, isPending: isUpdatePending } = useUpdateLeague();
  const { data, isLoading: isFetchLoading } = useFetchLeague();

  useEffect(() => {
    reset({
      nameLocal: data?.nameLocal,
      nameLatin: data?.nameLatin,
      abbreviationLocal: data?.abbreviationLocal,
      abbreviationLatin: data?.abbreviationLatin,
      countryCode: data?.countryCode,
      regionType: data?.regionType as RegionEnum,
    });
  }, [data, reset]);

  const title = useMemo(() => {
    return dataId
      ? `${formatMessage({
          id: "manage",
          defaultMessage: "Manage",
        })} ${data?.nameLocal ? `- ${data?.nameLocal}` : ""}`
      : `${formatMessage({
          id: "addLeague",
          defaultMessage: "Adding a New League",
        })}`;
  }, [dataId, formatMessage, data]);

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    if (dataId) {
      // @ts-ignore
      updateFn(data);
    } else {
      // @ts-ignore
      createFn(data).then(() => navigate(`/${customerId}/admin/${sport}/${organizationId}/leagues`, { state: { search: data.nameLocal }}));
    }
  };
  return (
    <Page title={title} tabTitle={title}>
      {isCreatePending || isFetchLoading || isUpdatePending ? (
        <Loader />
      ) : (
        <Content>
          <form style={{ marginLeft: 20 }} onSubmit={handleSubmit(onSubmit)}>
            <Row style={{ maxWidth: 960 }} gutter={16}>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.local"
                      name="nameLocal"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.local.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "league",
                          }),
                        },
                      )}
                      rules={{
                        required: formatMessage(
                          {
                            id: "form.help.required.field",
                          },
                          {
                            field: formatMessage({
                              id: "name.local",
                            }),
                          },
                        ),
                      }}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                      errors={errors}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="abbreviationLocal"
                      labelTooltip={formatMessage({
                        id: "form.help.local.abbreviation",
                      })}
                      name="abbreviationLocal"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
                <FormControl
                  labelText="country"
                  name="countryCode"
                  rules={{
                    required: formatMessage(
                      { id: "form.help.required.field" },
                      {
                        field: formatMessage({
                          id: "country",
                        }),
                      },
                    ),
                  }}
                  control={control}
                  labelTooltip={formatMessage({
                    id: "form.help.league.country",
                  })}
                  render={({ field }) => (
                    <StyledFormSelect
                      style={{ width: "100%" }}
                      {...field}
                      options={countryCodes.map(([code, country]: CountryCodeEntry) => ({
                        value: code,
                        label: country,
                      }))}
                    />
                  )}
                  errors={errors}
                />
              </Col>
              <Col span={12}>
                <Row gutter={16}>
                  <Col span={16}>
                    <FormControl
                      labelText="name.latin"
                      name="nameLatin"
                      labelTooltip={formatMessage(
                        {
                          id: "form.help.latin.name.item",
                        },
                        {
                          item: formatMessage({
                            id: "league",
                          }),
                        },
                      )}
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                  <Col span={8}>
                    <FormControl
                      labelText="abbreviationLatin"
                      labelTooltip={formatMessage({
                        id: "form.help.latin.abbreviation",
                      })}
                      name="abbreviationLatin"
                      control={control}
                      render={({ field }) => <StyledFormInputs {...field} />}
                    />
                  </Col>
                </Row>
                <FormControl
                  labelText="regionType"
                  name="regionType"
                  control={control}
                  rules={{
                    required: formatMessage(
                      { id: "form.help.required.field" },
                      {
                        field: formatMessage({
                          id: "regionType",
                        }),
                      },
                    ),
                  }}
                  labelTooltip={formatMessage({
                    id: "form.help.league.region.type",
                  })}
                  render={({ field }) => (
                    <StyledFormSelect
                      style={{ width: "100%" }}
                      {...field}
                      options={Object.keys(RegionEnum).map((region) => ({
                        value: region,
                        label: region,
                      }))}
                    />
                  )}
                  errors={errors}
                />
              </Col>
            </Row>
            <PermissionCheck
              permissionKeys={[PermissionName]}
              permissionOptions={dataId ? [PermissionOperations.UPDATE] : [PermissionOperations.CREATE]}
            >
              <Button htmlType="submit" type="primary">
                {dataId ? formatMessage({ id: "league.update" }) : formatMessage({ id: "league.add" })}
              </Button>
            </PermissionCheck>
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/leagues`}>
              <Button style={{ marginLeft: 30, marginTop: 10 }} type="primary">
                {formatMessage({ id: "close" })}
              </Button>
            </Link>
          </form>
        </Content>
      )}
    </Page>
  );
};

export default ManageLeague;
